import React, { useEffect, useRef } from 'react';
import type { TechnicalData } from '@hub/models/data';
import Image from 'next/image';
import styles from './TechnicalTable.module.css';
import classNames from 'classnames';
import { fixMarkupLinks } from '@hub/libs/links.helper';
import { useTranslation } from 'next-i18next';
import { useRouter } from 'next/router';
// TechnicalTable and ProductMediaTeaser need to be refactored to solve this cyclic import
// eslint-disable-next-line import/no-cycle
import { ProductMediaTeaser } from '@hub/common/ProductMediaTeaser/ProductMediaTeaser';
import { MediaElementType } from '@hub/libs/widen-utils';
import { getLocale } from '@hub/libs/constants';

export interface TechnicalTableProps {
  data: TechnicalData[];
}

const TechnicalTableRow: React.FC<TechnicalData> = ({ description, value, icon, mediaElement }) => {
  const router = useRouter();
  const locale = getLocale(router);
  const { t } = useTranslation('product/productDetails');

  // this is necessary because dangerouslySetInnerHTML is bugged!
  const valueRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    if (!valueRef.current) {
      return;
    }

    if (typeof value === 'boolean') {
      valueRef.current.innerHTML = fixMarkupLinks(
        t(`PRODUCT_DETAILS.TECHNICAL_DATA_ATTRIBUTE_VALUE_${value ? 'TRUE' : 'FALSE'}`),
        locale
      );
    } else if (typeof value === 'string') {
      valueRef.current.innerHTML = fixMarkupLinks(value, locale);
    }
  }, [value, locale, t]);

  return (
    <tr className="border-b flex items-center justify-items-center flex-wrap">
      {!mediaElement?.widenId && (
        <td className={classNames('w-2/12', 'sm:w-1/12', '-mb-3', 'pl-4', 'py-2', 'md:pr-6', 'md:px-0')}>
          {icon && (
            <Image
              unoptimized
              className={styles.icon}
              alt={description}
              src={icon.startsWith('//') ? `https:${icon}` : icon}
              width={35}
              height={35}
              data-testid="technicaltable-icon"
              style={{
                maxWidth: '100%',
                height: 'auto',
              }}
            />
          )}
        </td>
      )}
      <td
        className={classNames(
          icon ? ['w-10/12', 'sm:w-11/12'] : 'w-full',
          'md:w-5/12',
          'p-4',
          !icon && 'pb-2',
          'md:p-6',
          icon ? 'md:pl-0' : 'md:pl-3'
        )}
      >
        {mediaElement?.widenId ? (
          <ProductMediaTeaser
            media={{ ...mediaElement, type: MediaElementType.DRAWING }}
            width={300}
            height={Math.round((300 / 3) * 2)}
          />
        ) : (
          description
        )}
      </td>
      <td className={classNames('w-full', 'md:w-6/12', 'px-4', 'pb-4', 'md:p-0', 'md:py-2')}>
        {mediaElement?.widenId && <h5 className="pb-2">{description} </h5>}
        <div ref={valueRef} />
      </td>
    </tr>
  );
};

export const TechnicalTable: React.FC<TechnicalTableProps> = ({ data }) => (
  <table className="w-full table-fixed">
    <tbody>
      {data.map(
        (row, i) =>
          row.value && (
            <TechnicalTableRow
              /* eslint-disable-next-line react/no-array-index-key */
              key={i}
              description={row.description}
              value={row.value}
              icon={row.icon}
              mediaElement={row.mediaElement}
            />
          )
      )}
    </tbody>
  </table>
);
